import { createBrowserUuid } from '@/shared/lib/helpers';

import type { KkmJournalCapture } from './bridge-transport';
import type { KkmJournalDeviceKind, KkmJournalEvent, KkmJournalSink } from './journal';
import type { KkmServerProtocolProfile, KkmServerTimeoutClass } from './profile';
import type {
  KkmAcquiringResult,
  KkmKktInfo,
  KkmServerBridge,
  KkmServerCommand,
  KkmServerDevice,
  KkmServerInfo,
  KkmServerResponse,
  KkmShiftCounters,
} from './protocol';

import { invokeKkmBridge } from './bridge-transport';
import { AMBIGUOUS_OUTCOME_MESSAGE, debugAdapterIssue, KkmServerAdapterError } from './errors';
import { getKkmJournalSink } from './journal';
import { KKM_SERVER_2_3_16_36_PROFILE } from './profile';
import {
  KkmProtocolValidationError,
  emptyShiftCounters,
  kkmCommandStatusConfirmsExecution,
  kkmCommandStatusIsPending,
  optionalFiniteNumber,
  parseKkmAcquiringResult,
  parseKkmDevices,
  parseKkmKktInfo,
  parseKkmServerInfo,
  parseKkmShiftCounters,
  vendorErrorCode,
} from './protocol';
import { redactKkmPayload } from './redact';

export type KkmCommandProgressKind = 'running';

export interface KkmCommandProgress {
  attempt: number;
  command: string;
  kind: KkmCommandProgressKind;
  operationId: string;
}

export type KkmNormalizedCommandErrorKind = 'device_error' | 'payment_declined' | 'user_cancelled';

export interface KkmNormalizedCommandError {
  kind: KkmNormalizedCommandErrorKind;
  message: string;
  outcome: 'known_not_run' | 'unknown' | 'user_cancelled';
  vendorCode: string | null;
  vendorMessage: string | null;
}

interface KkmTerminalResultBase {
  acquiring: KkmAcquiringResult | null;
  response: KkmServerResponse;
}

export type KkmExecutionResult =
  | (KkmTerminalResultBase & {
      kind: 'error';
      error: KkmNormalizedCommandError;
    })
  | (KkmTerminalResultBase & {
      kind: 'not_found';
    })
  | (KkmTerminalResultBase & {
      kind: 'ok';
    });

/**
 * Помечает вызов как фоновую диагностику. Журнал вправе дедуплицировать только
 * помеченные события: чтение, по которому принимается решение о смене, идёт теми же
 * командами класса `health`, и отличить его от опроса можно лишь по намерению вызывающего.
 */
export interface KkmDiagnosticOptions {
  diagnostic?: boolean;
}

export interface KkmExecuteOptions extends KkmDiagnosticOptions {
  deviceKey: string;
  onProgress?: (progress: KkmCommandProgress) => void;
  timeoutClass: KkmServerTimeoutClass;
}

export type KkmTerminalReadiness = 'ready' | 'unavailable';

export interface KkmHandshakeReady {
  devices: KkmServerDevice[];
  kind: 'ready';
  kkt: KkmKktInfo;
  kktDevice: KkmServerDevice;
  server: KkmServerInfo;
  terminals: Array<{
    device: KkmServerDevice;
    status: KkmTerminalReadiness;
  }>;
}

export type KkmHandshakeResult =
  | KkmHandshakeReady
  | {
      instruction: string;
      kind: 'bridge_missing' | 'component_missing';
    }
  | {
      instruction: string;
      kind: 'protocol_error';
    };

export interface KkmHandshakeOptions extends KkmDiagnosticOptions {
  kktDeviceId?: string;
}

export interface KkmShiftCommandInput {
  cashierName: string;
  cashierVatIn?: string | null;
  deviceId: string;
}

export type KkmTestPrintInput = KkmShiftCommandInput;

export interface KkmServerAdapterDependencies {
  getBridge?: () => KkmServerBridge | undefined;
  journal?: KkmJournalSink;
  nextId?: () => string;
  now?: () => number;
  profile?: KkmServerProtocolProfile;
}

interface ActiveOperation {
  command: KkmServerCommand;
  controller: AbortController;
}

export class KkmServerAdapter {
  private readonly activeOperations = new Map<string, ActiveOperation>();

  private readonly cancelledOperationIds = new Set<string>();

  private readonly getBridge: () => KkmServerBridge | undefined;

  private readonly journal: KkmJournalSink | undefined;

  private readonly nextId: () => string;

  private readonly now: () => number;

  private readonly profile: KkmServerProtocolProfile;

  public constructor(dependencies: KkmServerAdapterDependencies = {}) {
    this.getBridge = dependencies.getBridge ?? (() => (typeof window === 'undefined' ? undefined : window.KkmServer));
    this.journal = dependencies.journal;
    this.nextId =
      dependencies.nextId ??
      (() => {
        const id = createBrowserUuid();

        if (id === null) {
          debugAdapterIssue('operation_id_generation_failed', {
            technicalMessage: 'Browser crypto.randomUUID is unavailable.',
          });
          throw new KkmServerAdapterError({
            code: 'component_missing',
            command: null,
            operationId: null,
            outcome: 'known_not_run',
            safeMessage:
              'Браузер не поддерживает безопасную работу с кассой. Обновите его и откройте Морс по адресу с https.',
          });
        }

        return id;
      });
    this.now = dependencies.now ?? Date.now;
    this.profile = dependencies.profile ?? KKM_SERVER_2_3_16_36_PROFILE;
  }

  public async handshake(options: KkmHandshakeOptions = {}): Promise<KkmHandshakeResult> {
    try {
      const diagnostic = { diagnostic: options.diagnostic };
      const server = await this.getServerData(diagnostic);
      const devices = await this.listDevices(diagnostic);
      const kktDevice = selectKktDevice(devices, options.kktDeviceId);
      const kkt = await this.getKktData(kktDevice.idDevice, diagnostic);

      return {
        devices,
        kind: 'ready',
        kkt,
        kktDevice,
        server,
        terminals: devices
          .filter((device) => device.kind === 'acquiring')
          .map((device) => ({
            device,
            status: device.active && device.on ? 'ready' : 'unavailable',
          })),
      };
    } catch (error) {
      if (error instanceof KkmServerAdapterError) {
        if (error.code === 'bridge_missing') {
          return {
            instruction:
              'Браузер не видит расширение KKMServer. Проверьте, что оно установлено и включено в этом браузере, затем обновите страницу. Если это не кассовый компьютер — откройте Mors на нём.',
            kind: 'bridge_missing',
          };
        }

        if (error.code === 'component_missing') {
          return {
            instruction: error.message,
            kind: 'component_missing',
          };
        }

        return {
          instruction: error.message,
          kind: 'protocol_error',
        };
      }

      if (error instanceof KkmProtocolValidationError) {
        debugAdapterIssue('handshake_protocol_validation_failed', {
          technicalMessage: error.message,
        });
        return {
          instruction: 'Касса ответила неожиданно. Повторите проверку через несколько секунд.',
          kind: 'protocol_error',
        };
      }

      debugAdapterIssue('handshake_failed', {
        technicalErrorType: error instanceof Error ? error.name : typeof error,
      });
      return {
        instruction: 'Не удалось проверить кассу. Повторите проверку через несколько секунд.',
        kind: 'protocol_error',
      };
    }
  }

  public async getServerData(options: KkmDiagnosticOptions = {}): Promise<KkmServerInfo> {
    await this.waitForBridge();
    const result = await this.execute(
      {
        Command: 'GetServerData',
        IdCommand: this.nextId(),
        Timeout: this.profile.timeouts.health.commandTimeoutSeconds,
      },
      {
        deviceKey: 'server',
        diagnostic: options.diagnostic,
        timeoutClass: 'health',
      },
    );

    return parseKkmServerInfo(requireOk(result));
  }

  public async listDevices(options: KkmDiagnosticOptions = {}): Promise<KkmServerDevice[]> {
    const result = await this.execute(
      {
        Command: 'List',
        IdCommand: this.nextId(),
        Timeout: this.profile.timeouts.health.commandTimeoutSeconds,
      },
      {
        deviceKey: 'server',
        diagnostic: options.diagnostic,
        timeoutClass: 'health',
      },
    );

    return parseKkmDevices(requireOk(result));
  }

  public async getKktData(deviceId: string, options: KkmDiagnosticOptions = {}): Promise<KkmKktInfo> {
    const result = await this.execute(
      {
        Command: 'GetDataKKT',
        IdCommand: this.nextId(),
        IdDevice: deviceId,
        NumDevice: 0,
        Timeout: this.profile.timeouts.health.commandTimeoutSeconds,
      },
      {
        deviceKey: `kkt:${deviceId}`,
        diagnostic: options.diagnostic,
        timeoutClass: 'health',
      },
    );

    const response = requireOk(result);

    try {
      return parseKkmKktInfo(response);
    } catch (error) {
      if (!(error instanceof KkmProtocolValidationError)) {
        throw error;
      }

      // KKMServer может вернуть Status=0 без Info, когда данные ККТ недоступны.
      throw new KkmServerAdapterError({
        code: 'malformed_response',
        command: response.command,
        operationId: response.idCommand,
        outcome: 'known_not_run',
        safeMessage:
          'KKMServer отвечает, но не передал состояние кассы. Проверьте питание и подключение кассы и убедитесь, что запущен только один экземпляр KKMServer. Если ошибка сохраняется — свяжитесь с управляющим.',
      });
    }
  }

  public async getCounters(deviceId: string): Promise<KkmShiftCounters> {
    const result = await this.execute(
      {
        Command: 'GetCounters',
        IdCommand: this.nextId(),
        IdDevice: deviceId,
        NumDevice: 0,
        Timeout: this.profile.timeouts.health.commandTimeoutSeconds,
        СountersType: 'Shift',
      },
      {
        deviceKey: `kkt:${deviceId}`,
        timeoutClass: 'health',
      },
    );

    // ККТ на ФФД 1.05 отвечает `Status=2: «ККТ не поддерживает формат ФФД 1.1 и выше»` (ТК Питер).
    // Счётчики смены справочные: отказ ККТ — это «нет данных», а не «касса недоступна».
    return result.kind === 'error' ? emptyShiftCounters() : parseKkmShiftCounters(requireOk(result));
  }

  /**
   * Документ фискального накопителя по номеру ФД (`0` — последний) без печати копии.
   * Отказ кассы означает «не прочитано», а не «документа нет».
   */
  public async getDataCheck(deviceId: string, fiscalNumber: number): Promise<KkmServerResponse | null> {
    const result = await this.execute(
      {
        Command: 'GetDataCheck',
        IdCommand: this.nextId(),
        IdDevice: deviceId,
        NumDevice: 0,
        FiscalNumber: fiscalNumber,
        NumberCopies: 0,
        Timeout: this.profile.timeouts.health.commandTimeoutSeconds,
      },
      {
        deviceKey: `kkt:${deviceId}`,
        timeoutClass: 'health',
      },
    );

    return result.kind === 'ok' ? result.response : null;
  }

  /**
   * Проверка кодов маркировки ККТ и ТС ПИоТ без печати чека: до оплаты картой
   * и на приёмке товара.
   */
  public async validateMarkingCodes(
    deviceId: string,
    codes: readonly string[],
    options: KkmDiagnosticOptions & { name?: string } = {},
  ): Promise<KkmExecutionResult> {
    return this.execute(
      {
        Command: 'ValidationMarkingCode',
        IdCommand: this.nextId(),
        IdDevice: deviceId,
        NumDevice: 0,
        Timeout: this.profile.timeouts.kkt.commandTimeoutSeconds,
        GoodCodeDatas: codes.map((code) => ({ Barcode: code, Name: options.name ?? '', Quantity: 1 })),
      },
      {
        deviceKey: `kkt:${deviceId}`,
        diagnostic: options.diagnostic,
        timeoutClass: 'kkt',
      },
    );
  }

  public async execute(command: KkmServerCommand, options: KkmExecuteOptions): Promise<KkmExecutionResult> {
    assertCommandIdentity(command);

    if (this.activeOperations.has(options.deviceKey)) {
      throw new KkmServerAdapterError({
        code: 'device_busy',
        command: command.Command,
        operationId: command.IdCommand,
        outcome: 'known_not_run',
        safeMessage: 'Касса занята другой операцией. Дождитесь её завершения и попробуйте снова.',
      });
    }

    const active: ActiveOperation = {
      command,
      controller: new AbortController(),
    };
    this.activeOperations.set(options.deviceKey, active);

    try {
      const response = await this.executeLoop(command, options, active.controller.signal);

      return this.toExecutionResult(response);
    } finally {
      this.activeOperations.delete(options.deviceKey);
      this.cancelledOperationIds.delete(command.IdCommand);
    }
  }

  /**
   * Чтение результата уже отправленной команды. Сбой самого чтения — нет моста, касса
   * занята, повреждённый или чужой ответ, timeout — ничего не доказывает об исполнении
   * исходной команды, поэтому `known_not_run` здесь не бывает.
   */
  public async getResult(idCommand: string, options: KkmExecuteOptions): Promise<KkmExecutionResult> {
    const command: KkmServerCommand = { Command: 'GetRezult', IdCommand: idCommand };

    try {
      return await this.execute(command, options);
    } catch (error) {
      throw escalateAcceptedCommandError(error, command);
    }
  }

  public async openShift(input: KkmShiftCommandInput): Promise<KkmExecutionResult> {
    return this.execute(shiftCommand('OpenShift', input, this.nextId(), this.profile), {
      deviceKey: `kkt:${input.deviceId}`,
      timeoutClass: 'shift',
    });
  }

  /**
   * `idCommand` передаётся при восстановлении: повтор идёт с тем же correlation ID,
   * поэтому `GetRezult` и журнал остаются связаны с исходной попыткой Z-отчёта.
   */
  public async closeShift(input: KkmShiftCommandInput): Promise<KkmExecutionResult> {
    return this.execute(shiftCommand('CloseShift', input, this.nextId(), this.profile), {
      deviceKey: `kkt:${input.deviceId}`,
      timeoutClass: 'shift',
    });
  }

  public async xReport(deviceId: string): Promise<KkmExecutionResult> {
    return this.execute(
      {
        Command: 'XReport',
        IdCommand: this.nextId(),
        IdDevice: deviceId,
        NumDevice: 0,
        Timeout: this.profile.timeouts.shift.commandTimeoutSeconds,
      },
      {
        deviceKey: `kkt:${deviceId}`,
        timeoutClass: 'shift',
      },
    );
  }

  public async printTest(input: KkmTestPrintInput): Promise<KkmExecutionResult> {
    return this.execute(
      {
        Cash: 0,
        CashProvision: 0,
        CheckStrings: [],
        Command: 'RegisterCheck',
        Credit: 0,
        ElectronicPayment: 0,
        IdCommand: this.nextId(),
        IdDevice: input.deviceId,
        IsFiscalCheck: false,
        NotPrint: false,
        NumDevice: 0,
        PayByProcessing: false,
        TaxVariant: 1,
        Timeout: this.profile.timeouts.kkt.commandTimeoutSeconds,
        TypeCheck: 0,
        AdvancePayment: 0,
        CashierName: input.cashierName,
        ...cashierVatIn(input.cashierVatIn),
      },
      {
        deviceKey: `kkt:${input.deviceId}`,
        timeoutClass: 'kkt',
      },
    );
  }

  public async cancelActiveOperation(deviceKey: string): Promise<KkmExecutionResult> {
    const active = this.activeOperations.get(deviceKey);
    const cancelFactory = this.profile.cancelActiveCommand;

    if (active === undefined || cancelFactory === null) {
      throw new KkmServerAdapterError({
        code: 'cancellation_unsupported',
        command: active?.command.Command ?? null,
        operationId: active?.command.IdCommand ?? null,
        outcome: 'unknown',
        safeMessage: 'Не удалось безопасно отменить операцию. Не повторяйте оплату вручную: Mors проверит результат.',
      });
    }

    const cancellationCommand = cancelFactory(active.command, this.nextId());
    const cancellationController = new AbortController();
    const response = await this.executeLoop(
      cancellationCommand,
      {
        deviceKey: `${deviceKey}:cancellation`,
        timeoutClass: 'acquiring',
      },
      cancellationController.signal,
    );
    const result = this.toExecutionResult(response);

    if (result.kind === 'ok') {
      this.cancelledOperationIds.add(active.command.IdCommand);
      active.controller.abort();
    }

    return result;
  }

  private async waitForBridge(): Promise<KkmServerBridge> {
    const deadline = this.now() + this.profile.bridgeInjectionWatchdogMs;

    while (this.now() < deadline) {
      const bridge = this.getBridge();

      if (bridge !== undefined && typeof bridge.Execute === 'function') {
        return bridge;
      }

      await sleep(50);
    }

    throw new KkmServerAdapterError({
      code: 'bridge_missing',
      command: null,
      operationId: null,
      outcome: 'known_not_run',
      safeMessage:
        'Браузер не видит расширение KKMServer. Проверьте, что оно установлено и включено в этом браузере, затем обновите страницу. Если это не кассовый компьютер — откройте Mors на нём.',
    });
  }

  private async executeLoop(
    command: KkmServerCommand,
    options: KkmExecuteOptions,
    signal: AbortSignal,
  ): Promise<KkmServerResponse> {
    const timeoutMs = this.profile.timeouts[options.timeoutClass].watchdogMs;
    const deadline = this.now() + timeoutMs;
    let attempt = 0;
    let acceptedByDevice = false;

    try {
      let response = await this.invoke(command, options, attempt, remainingMs(deadline, this.now(), command), signal);

      while (kkmCommandStatusIsPending(response.status)) {
        // Команда принята KKMServer: последующий сбой уже не доказывает отказ.
        acceptedByDevice = true;
        attempt += 1;
        options.onProgress?.({
          attempt,
          command: command.Command,
          kind: 'running',
          operationId: command.IdCommand,
        });
        await sleepWithSignal(
          Math.min(this.profile.pollIntervalMs, remainingMs(deadline, this.now(), command)),
          signal,
          command,
          this.cancelledOperationIds,
        );
        response = await this.invoke(
          {
            Command: 'GetRezult',
            IdCommand: command.IdCommand,
          },
          options,
          attempt,
          remainingMs(deadline, this.now(), command),
          signal,
        );
      }

      return response;
    } catch (error) {
      throw acceptedByDevice ? escalateAcceptedCommandError(error, command) : error;
    }
  }

  private invoke(
    command: KkmServerCommand,
    options: KkmExecuteOptions,
    attempt: number,
    timeoutMs: number,
    signal: AbortSignal,
  ): Promise<KkmServerResponse> {
    return invokeKkmBridge({
      bridge: this.getBridge(),
      command,
      capture: (capture) => this.captureJournal(command, options, attempt, capture),
      isCancelled: () => this.cancelledOperationIds.has(command.IdCommand),
      now: this.now,
      signal,
      timeoutMs,
    });
  }

  private captureJournal(
    command: KkmServerCommand,
    options: KkmExecuteOptions,
    attempt: number,
    capture: KkmJournalCapture,
  ): void {
    try {
      const event: KkmJournalEvent = {
        attempt,
        clientAt: new Date(this.now()).toISOString(),
        command: command.Command,
        deviceKind: journalDeviceKind(options.deviceKey),
        diagnostic: options.diagnostic === true,
        durationMs: capture.startedAt === undefined ? null : Math.max(0, Math.round(this.now() - capture.startedAt)),
        errorCode: capture.errorCode ?? null,
        idCommand: command.IdCommand,
        idDevice: journalDeviceId(command, options.deviceKey),
        payload: redactKkmPayload(capture.payload),
        phase: capture.phase,
        status: capture.status ?? null,
        vendorMessage:
          capture.vendorMessage === undefined ? null : (redactKkmPayload(capture.vendorMessage) as string | null),
      };

      (this.journal ?? getKkmJournalSink()).record(event);
    } catch {
      // Сбой редакции или sink намеренно не влияет на команду ККТ/терминала.
    }
  }

  private toExecutionResult(response: KkmServerResponse): KkmExecutionResult {
    const acquiring = isAcquiringCommand(response.command) ? parseKkmAcquiringResult(response) : null;

    if (kkmCommandStatusConfirmsExecution(response.status)) {
      return {
        acquiring,
        kind: 'ok',
        response,
      };
    }

    if (response.status === 3) {
      return {
        acquiring,
        kind: 'not_found',
        response,
      };
    }

    const code = vendorErrorCode(response);
    // Вендорский код читаем только у эквайринговой команды, чей ответ не несёт следов
    // проведённой операции: такой исход остаётся неопределённым, чем бы терминал его ни назвал.
    const bankEvidence = acquiring !== null && hasKkmBankEvidence(acquiring, response);
    const bankCode = acquiring !== null && !bankEvidence ? code : null;
    const cancelled =
      this.cancelledOperationIds.has(response.idCommand) ||
      (bankCode !== null && this.profile.paymentCancelCodes.includes(bankCode));
    const declined = bankCode !== null && this.profile.paymentDeclineCodes.includes(bankCode);
    const technicalMessage = technicalVendorMessage(response);
    debugAdapterIssue('device_command_failed', {
      bankEvidence,
      classification: cancelled ? 'user_cancelled' : declined ? 'payment_declined' : 'unknown',
      command: response.command,
      technicalMessage,
      vendorCode: code,
    });

    return {
      acquiring,
      error: {
        kind: cancelled ? 'user_cancelled' : declined ? 'payment_declined' : 'device_error',
        message: cancelled
          ? 'Операция отменена.'
          : declined
            ? declinedMessage(response.command)
            : safeVendorMessage(response),
        outcome: cancelled ? 'user_cancelled' : declined ? 'known_not_run' : 'unknown',
        vendorCode: code,
        vendorMessage: technicalMessage,
      },
      kind: 'error',
      response,
    };
  }
}

function journalDeviceKind(deviceKey: string): KkmJournalDeviceKind {
  if (deviceKey.startsWith('terminal:')) {
    return 'acquiring';
  }

  if (deviceKey.startsWith('kkt:')) {
    return 'kkt';
  }

  return 'server';
}

function journalDeviceId(command: KkmServerCommand, deviceKey: string): string | null {
  if (typeof command.IdDevice === 'string' && command.IdDevice.trim() !== '') {
    return command.IdDevice;
  }

  if (journalDeviceKind(deviceKey) === 'server') {
    return null;
  }

  return deviceKey.split(':')[1] ?? null;
}

/**
 * Ошибка, случившаяся после подтверждённого `Status=1 (Run)`/`Status=4 (NotRun)` или при
 * чтении результата уже отправленной команды, не может оставаться `known_not_run`: касса
 * уже могла напечатать чек или провести оплату. Единственное исключение — `user_cancelled`:
 * это отдельный подтверждённый исход, а не догадка.
 */
function escalateAcceptedCommandError(error: unknown, command: KkmServerCommand): unknown {
  if (!(error instanceof KkmServerAdapterError) || error.outcome !== 'known_not_run') {
    return error;
  }

  return new KkmServerAdapterError({
    code: error.code,
    command: error.command ?? command.Command,
    operationId: error.operationId ?? command.IdCommand,
    outcome: 'unknown',
    safeMessage: AMBIGUOUS_OUTCOME_MESSAGE,
  });
}

function assertCommandIdentity(command: KkmServerCommand): void {
  if (command.IdCommand.trim() === '' || command.IdCommand.length > 40 || command.Command.trim() === '') {
    debugAdapterIssue('invalid_command_identity', {
      command: command.Command,
      technicalMessage: 'Command or IdCommand violates the local protocol profile.',
    });
    throw new KkmServerAdapterError({
      code: 'unsupported_mapping',
      command: command.Command,
      operationId: command.IdCommand,
      outcome: 'known_not_run',
      safeMessage: 'Не удалось безопасно подготовить операцию. Обновите страницу и повторите.',
    });
  }
}

function requireOk(result: KkmExecutionResult): KkmServerResponse {
  if (result.kind !== 'ok') {
    const technicalMessage =
      result.kind === 'error'
        ? (technicalVendorMessage(result.response) ?? 'The local device command failed without a vendor message.')
        : `Command status: ${result.kind}`;
    const failedToFetch = /failed to fetch/i.test(technicalMessage);
    const code = failedToFetch || componentMissing(technicalMessage) ? 'component_missing' : 'malformed_response';
    debugAdapterIssue('readiness_command_failed', {
      code,
      command: result.response.command,
      technicalMessage,
    });

    throw new KkmServerAdapterError({
      code,
      command: result.response.command,
      operationId: result.response.idCommand,
      outcome: 'known_not_run',
      safeMessage: failedToFetch
        ? 'Нет связи с KKMServer. В меню расширений этого браузера откройте KKM Server, выберите «Другой сервер…», введите http://localhost:5894 и нажмите Tab для сохранения. Затем обновите Mors. Если не помогло — проверьте, запущена ли программа KKMServer.'
        : code === 'component_missing'
          ? 'Программа KKMServer на этом компьютере не отвечает. Проверьте, что она запущена — значок KKMServer в системном трее Windows, — при необходимости запустите её заново.'
          : 'Не удалось получить состояние кассы. Повторите проверку через несколько секунд.',
    });
  }

  return result.response;
}

function selectKktDevice(devices: KkmServerDevice[], requestedId: string | undefined): KkmServerDevice {
  const candidates = devices.filter(
    (device) => device.kind === 'kkt' && (requestedId === undefined || device.idDevice === requestedId),
  );
  const selected = candidates[0];

  if (selected === undefined || candidates.length !== 1) {
    debugAdapterIssue('kkt_selection_failed', {
      candidatesCount: candidates.length,
      requestedDevice: requestedId === undefined ? 'not_specified' : 'specified',
    });
    // Текст доходит до кассира как есть: несовпадение с привязкой чинится переподключением,
    // а не повтором проверки (новый IdDevice после переустановки KKMServer, другая ККТ).
    throw new KkmServerAdapterError({
      code: 'kkt_not_found',
      command: 'List',
      operationId: null,
      outcome: 'known_not_run',
      safeMessage:
        requestedId !== undefined
          ? 'К компьютеру подключена другая касса, не та, что привязана к локации. Переподключите кассу в настройках фискализации.'
          : candidates.length === 0
            ? 'KKMServer не видит кассу. Проверьте её питание и подключение к компьютеру.'
            : 'К компьютеру подключено несколько касс. Выберите нужную в настройках фискализации.',
    });
  }

  return selected;
}

function shiftCommand(
  command: 'CloseShift' | 'OpenShift',
  input: KkmShiftCommandInput,
  idCommand: string,
  profile: KkmServerProtocolProfile,
): KkmServerCommand {
  return {
    CashierName: input.cashierName,
    Command: command,
    IdCommand: idCommand,
    IdDevice: input.deviceId,
    NotPrint: false,
    NumDevice: 0,
    TaxVariant: 1,
    // Без явного `Timeout` KKMServer ждёт свой default, который может пережить
    // browser watchdog: вкладка бросит операцию, пока ККТ ещё печатает Z-отчёт.
    Timeout: profile.timeouts.shift.commandTimeoutSeconds,
    ...cashierVatIn(input.cashierVatIn),
  };
}

function cashierVatIn(value: string | null | undefined): Record<string, unknown> {
  const normalized = value?.trim();

  return normalized === undefined || normalized === '' ? {} : { CashierVATIN: normalized };
}

function isAcquiringCommand(command: string): boolean {
  return (
    command === 'PayByPaymentCard' ||
    command === 'ReturnPaymentByPaymentCard' ||
    command === 'CancelPaymentByPaymentCard'
  );
}

function declinedMessage(command: string): string {
  return command === 'ReturnPaymentByPaymentCard'
    ? 'Банк отклонил возврат. Деньги на карту не вернулись.'
    : 'Банк отклонил операцию. Выберите другой способ оплаты.';
}

function safeVendorMessage(response: KkmServerResponse): string {
  return isAcquiringCommand(response.command)
    ? 'Неясно, завершилась ли операция с картой. Не повторяйте её вручную: Mors проверит результат.'
    : 'Касса не завершила операцию. Не повторяйте её вручную: Mors проверит результат.';
}

/**
 * RRN, код авторизации или ненулевой `Amount` в ответе терминала: деньги могли уйти.
 * Подтверждённые отказы стенда приходят с `Amount 0`; отсутствующий `Amount` следом не считается.
 */
export function hasKkmBankEvidence(acquiring: KkmAcquiringResult | null, response: KkmServerResponse): boolean {
  const amount = optionalFiniteNumber(response.raw.Amount);

  return acquiring?.rrn != null || acquiring?.authorizationCode != null || (amount !== null && amount !== 0);
}

/**
 * Текст вендора для оператора и журнала: без `<br/>`, без .NET stack из `Message … Trase`,
 * длинные числовые последовательности замаскированы, длина ограничена.
 */
export function technicalVendorMessage(response: KkmServerResponse): string | null {
  const messages = [response.error, response.message, response.warning]
    .filter((value): value is string => value !== null)
    .map((value) =>
      value
        .replaceAll(/<br\s*\/?>/gi, ' ')
        .replaceAll(/\s+/g, ' ')
        .trim(),
    )
    .filter((value) => value !== '');
  const joined = messages.join(' ');
  // Исключение KKMServer приходит как `Message: <текст> Trase: <стек .NET>`; оператору нужен только текст.
  const message = /^Message:\s*(.+?)\s*Trase:/.exec(joined)?.[1] ?? joined;

  return message === '' ? null : message.replaceAll(/[0-9]{6,}/g, '***').slice(0, 500);
}

function componentMissing(message: string): boolean {
  return /компонент|component/i.test(message);
}

function remainingMs(deadline: number, now: number, command: KkmServerCommand): number {
  const remaining = deadline - now;

  if (remaining <= 0) {
    // Correlation ID обязателен: без него reconciliation не может выполнить `GetRezult`
    // по той же команде и выяснить, чем она закончилась на устройстве.
    throw new KkmServerAdapterError({
      code: 'operation_timeout',
      command: command.Command,
      operationId: command.IdCommand,
      outcome: 'unknown',
      safeMessage: AMBIGUOUS_OUTCOME_MESSAGE,
    });
  }

  return remaining;
}

function sleep(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, timeoutMs);
  });
}

function sleepWithSignal(
  timeoutMs: number,
  signal: AbortSignal,
  command: KkmServerCommand,
  cancelledOperationIds: ReadonlySet<string>,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => {
        signal.removeEventListener('abort', handleAbort);
        resolve();
      },
      Math.max(1, timeoutMs),
    );
    const handleAbort = (): void => {
      clearTimeout(timer);
      reject(
        new KkmServerAdapterError({
          code: 'operation_timeout',
          command: command.Command,
          operationId: command.IdCommand,
          outcome: cancelledOperationIds.has(command.IdCommand) ? 'user_cancelled' : 'unknown',
          safeMessage: cancelledOperationIds.has(command.IdCommand)
            ? 'Операция отменена пользователем.'
            : AMBIGUOUS_OUTCOME_MESSAGE,
        }),
      );
    };

    signal.addEventListener('abort', handleAbort, { once: true });
  });
}
